import React, { useState } from "react";
import axios from "axios";
import { Box, Button, Typography } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setCredentials } from "state/authSlice";
import { api } from "state/api";
import { createLogger, summarizeError } from "../utils/logger";

const log = createLogger("ui.ImpersonationBanner");

// Sticky banner shown while an Admin is "logged in as" a client. Renders
// nothing in normal sessions (`auth.impersonator` is null then).
function ImpersonationBanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((s) => s.auth.token);
  const user = useSelector((s) => s.auth.user);
  const impersonator = useSelector((s) => s.auth.impersonator);

  const [exiting, setExiting] = useState(false);
  const [exitFailed, setExitFailed] = useState(false);

  if (!impersonator) return null;

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    setExitFailed(false);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}usersServer/exit-impersonation`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          // Without a timeout, a request that stalls in the network layer
          // never settles, leaving the bar stuck on "Exiting…" with the
          // button disabled until a manual reload.
          timeout: 15000,
        }
      );
      const { user: adminUser, token: adminToken } = res.data;
      localStorage.setItem(
        "user",
        JSON.stringify({ user: adminUser, token: adminToken })
      );
      dispatch(
        setCredentials({
          user: adminUser,
          token: adminToken,
          impersonator: null,
        })
      );
      dispatch(api.util.resetApiState());
      navigate("/clients");
    } catch (err) {
      log.error("exit.failed", { ...summarizeError(err) });
      setExitFailed(true);
      setExiting(false);
    }
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "8px 16px",
        background: "#7B1FA2",
        color: "#fff",
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
        Signed in as{" "}
        <strong>{user?.name || user?.email || "client"}</strong>
        {impersonator?.name && (
          <> on behalf of <strong>{impersonator.name}</strong></>
        )}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        {exitFailed && (
          <Typography sx={{ fontSize: "0.8rem", opacity: 0.9 }}>
            Couldn't exit — check your connection and try again.
          </Typography>
        )}
        <Button
          size="small"
          variant="contained"
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleExit}
          disabled={exiting}
          sx={{
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            "&:hover": { background: "rgba(255,255,255,0.28)" },
          }}
        >
          {exiting ? "Exiting…" : "Exit impersonation"}
        </Button>
      </Box>
    </Box>
  );
}

export default ImpersonationBanner;
